import { useEffect, useRef, useCallback } from 'react'
import { bulkMarkRead } from '../lib/api'

/**
 * Auto-marks unread notifications as read after they've been visible for a delay.
 * Uses IntersectionObserver to detect visibility. Batches API calls.
 *
 * @param onMarkedRead - Called with IDs after they are successfully marked read
 * @param delay - How long (ms) a notification must be visible before marking read
 * @param root - Scrollable container element for the observer (null = viewport)
 */
export function useAutoMarkRead(
  onMarkedRead?: (ids: number[]) => void,
  delay = 2000,
  root?: Element | null,
) {
  const pendingTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const readyToFlush = useRef<Set<number>>(new Set())
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const elementsRef = useRef<Map<number, Element>>(new Map())
  const onMarkedReadRef = useRef(onMarkedRead)

  useEffect(() => {
    onMarkedReadRef.current = onMarkedRead
  }, [onMarkedRead])

  const flush = useCallback(async () => {
    const ids = Array.from(readyToFlush.current)
    if (ids.length === 0) return
    try {
      await bulkMarkRead(ids)
      // Clear only after success
      for (const id of ids) readyToFlush.current.delete(id)
      onMarkedReadRef.current?.(ids)
    } catch {
      // Keep IDs in readyToFlush for next attempt
    }
  }, [])

  const scheduleFlush = useCallback(() => {
    if (flushTimer.current) return
    flushTimer.current = setTimeout(() => {
      flushTimer.current = null
      flush()
    }, 500)
  }, [flush])

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = Number(entry.target.getAttribute('data-notification-id'))
          if (!id) continue

          if (entry.isIntersecting) {
            if (!pendingTimers.current.has(id)) {
              const timer = setTimeout(() => {
                pendingTimers.current.delete(id)
                readyToFlush.current.add(id)
                scheduleFlush()
              }, delay)
              pendingTimers.current.set(id, timer)
            }
          } else {
            const timer = pendingTimers.current.get(id)
            if (timer) {
              clearTimeout(timer)
              pendingTimers.current.delete(id)
            }
          }
        }
      },
      { threshold: 0.5, root: root ?? null },
    )

    for (const el of elementsRef.current.values()) {
      observerRef.current.observe(el)
    }

    const timers = pendingTimers.current

    return () => {
      observerRef.current?.disconnect()
      for (const timer of timers.values()) {
        clearTimeout(timer)
      }
      timers.clear()
      if (flushTimer.current) {
        clearTimeout(flushTimer.current)
        flushTimer.current = null
      }
    }
  }, [delay, scheduleFlush, flush, root])

  const observe = useCallback((id: number, element: HTMLElement | null) => {
    const prev = elementsRef.current.get(id)
    if (prev && observerRef.current) {
      observerRef.current.unobserve(prev)
    }

    if (element) {
      elementsRef.current.set(id, element)
      element.setAttribute('data-notification-id', String(id))
      observerRef.current?.observe(element)
    } else {
      elementsRef.current.delete(id)
    }
  }, [])

  return { observe }
}
